import Bowser from 'bowser';
import tools from 'utilities/tools';

export const createBrowserInstance = () => Bowser.parse(window.navigator.userAgent);

export const PLATFORMS_MAP = {
    tablet: 'tablet',
    mobile: 'mobile',
    desktop: 'desktop',
    tv: 'tv',
};

export const BROWSER_MAP = {
    android: 'Android Browser',
    blackberry: 'BlackBerry',
    chrome: 'Chrome',
    chromium: 'Chromium',
    electron: 'Electron',
    firefox: 'Firefox',
    ie: 'Internet Explorer',
    edge: 'Microsoft Edge',
    opera: 'Opera',
    safari: 'Safari',
    samsung: 'Samsung Internet for Android',
    vivaldi: 'Vivaldi',
    webos: 'WebOS Browser',
    miui: 'Miui',
};

export const CUSTOM_BROWSER_MAP = {
    huawei: 'Huawei',
    samsung: 'Samsung Internet',
};

export const OS_MAP = {
    windowsPhone: 'Windows Phone',
    windows: 'Windows',
    macos: 'macOS',
    ios: 'iOS',
    android: 'Android',
    webos: 'WebOS',
    blackberry: 'BlackBerry',
    linux: 'Linux',
    chromeos: 'Chrome OS',
};

export const CUSTOM_OS_MAP = {
    miui: 'Miui',
};

export const ENGINE_MAP = {
    edgeHTML: 'EdgeHTML',
    blink: 'Blink',
    trident: 'Trident',
    presto: 'Presto',
    gecko: 'Gecko',
    webkit: 'WebKit',
};

const IOS_CHROME_MIN_VERSION = 90;
const IOS_OS_MIN_VERSION = 14;

export const getBrowserName = () => createBrowserInstance().browser.name;
export const getBrowserVersion = () => createBrowserInstance().browser.version;
export const getOSName = () => createBrowserInstance().os.name;
export const getOSVersion = () => createBrowserInstance().os.version;
export const getOSVersionName = () => createBrowserInstance().os.versionName;
export const getPlatformModel = () => createBrowserInstance().platform.model;
export const getPlatformType = () => createBrowserInstance().platform.type;
export const getPlatformVendor = () => createBrowserInstance().platform.vendor;
export const getEngineName = () => createBrowserInstance().engine.name;
export const getEngineVersion = () => createBrowserInstance().engine.version;

export const isChrome = () => getBrowserName() === BROWSER_MAP.chrome;
export const isFirefox = () => getBrowserName() === BROWSER_MAP.firefox;
export const isSafari = () => getBrowserName() === BROWSER_MAP.safari;
export const isEdge = () => getBrowserName() === BROWSER_MAP.edge;
export const isInternetExplorer = () => getBrowserName() === BROWSER_MAP.ie;
export const isMiBrowser = () => getBrowserName() === BROWSER_MAP.miui || /MIUI/i.test(navigator.userAgent);

export const isBlink = () => getEngineName() === ENGINE_MAP.blink;
export const isGecko = () => getEngineName() === ENGINE_MAP.gecko;
export const isWebKit = () => getEngineName() === ENGINE_MAP.webkit;
// Why undefined? https://github.com/faisalman/ua-parser-js/issues/224#issuecomment-282257256
export const isDesktop = () => getPlatformType() === PLATFORMS_MAP.desktop || getPlatformType() === undefined;
export const isTablet = () => getPlatformType() === PLATFORMS_MAP.tablet;
export const isMobile = () => getPlatformType() === PLATFORMS_MAP.mobile;

export const isIos = () => getOSName() === OS_MAP.ios;
export const isAndroid = () => getOSName() === OS_MAP.android;
export const isWindows = () => getOSName() === OS_MAP.windows;
export const isMacOS = () => getOSName() === OS_MAP.macos;
export const isLinux = () => getOSName() === OS_MAP.linux;

export const isXiaomiDevice = () => {
    const miuiRegExp = /MI [0-9]+|Redmi [0-9]+|POCO [0-9]+/i;

    return miuiRegExp.test(navigator.userAgent);
};

export const isHuawei = () => {
    const huaweiRegExp = /huawei/i;

    return huaweiRegExp.test(navigator.userAgent);
};

export const isIphone = () => isMobile() && isIos();
export const isIpad = () => isTablet() && isIos();

export const isInvalidUserAgent = navUserAgent => {
    const invalidUserAgents = [
        '; WV)', //webview android
        '(KHTML, LIKE GECKO) MOBILE', //webview ios
        'XIAOMI/MIUIBROWSER', // xiaomi browser
    ];
    const invalid = invalidUserAgents.filter(userAgent => navUserAgent.toUpperCase().includes(userAgent));

    return invalid.length > 0;
};

export const isValidBrowserForIos = isIosChromeActivated => {
    const { isEqualOrHigherVersion } = tools;
    const isValidChromeVersion = isEqualOrHigherVersion(getBrowserVersion(), IOS_CHROME_MIN_VERSION);
    const isValidIOSVersion = isEqualOrHigherVersion(getOSVersion() ?? getOSVersionName(), IOS_OS_MIN_VERSION);

    if (!isIos() && !isMacOS()) {
        return true;
    }

    if (isIphone() || isIpad()) {
        if (isFirefox()) {
            return false;
        }

        if (isChrome()) {
            return isIosChromeActivated ? isValidChromeVersion && isValidIOSVersion : false;
        }

        return true;
    }

    if (isChrome()) {
        return isIosChromeActivated ? isValidChromeVersion : false;
    }

    return true;
};
